import { formatHexColor } from './editorUnits';

export type Rgb = { r: number; g: number; b: number };

/** HSL with hue in degrees [0, 360), saturation and lightness in [0, 1]. */
export type Hsl = { h: number; s: number; l: number };

const WHITE: Rgb = { r: 255, g: 255, b: 255 };

function clampByte(value: number): number {
  return Math.max(0, Math.min(255, Math.round(value)));
}

export function parseCssColorToRgb(input: string | null | undefined): Rgb | null {
  if (!input) return null;
  const value = input.trim().toLowerCase();
  if (!value || value === 'none' || value === 'transparent' || value === 'currentcolor') {
    return null;
  }

  const hexMatch = value.match(/^#([0-9a-f]{3,8})$/i);
  if (hexMatch) {
    const hex = hexMatch[1];
    if (hex.length === 3) {
      return {
        r: parseInt(hex[0] + hex[0], 16),
        g: parseInt(hex[1] + hex[1], 16),
        b: parseInt(hex[2] + hex[2], 16),
      };
    }
    if (hex.length >= 6) {
      return {
        r: parseInt(hex.slice(0, 2), 16),
        g: parseInt(hex.slice(2, 4), 16),
        b: parseInt(hex.slice(4, 6), 16),
      };
    }
  }

  const rgbMatch = value.match(/^rgba?\(\s*([^)]+)\)/i);
  if (rgbMatch) {
    const parts = rgbMatch[1].split(/[,\s/]+/).map((part) => part.trim());
    const r = parseFloat(parts[0]);
    const g = parseFloat(parts[1]);
    const b = parseFloat(parts[2]);
    const alpha = parts[3] != null ? parseFloat(parts[3]) : 1;
    if (![r, g, b, alpha].every((n) => Number.isFinite(n))) return null;
    return compositeOnWhite({ r, g, b }, alpha);
  }

  return null;
}

export function compositeOnWhite(rgb: Rgb, alpha: number): Rgb {
  const a = Math.max(0, Math.min(1, alpha));
  return {
    r: clampByte(rgb.r * a + WHITE.r * (1 - a)),
    g: clampByte(rgb.g * a + WHITE.g * (1 - a)),
    b: clampByte(rgb.b * a + WHITE.b * (1 - a)),
  };
}

export function rgbToHex(rgb: Rgb): string {
  const toHex = (n: number) => clampByte(n).toString(16).padStart(2, '0');
  return `#${toHex(rgb.r)}${toHex(rgb.g)}${toHex(rgb.b)}`;
}

export function parseCssColorToHex(input: string | null | undefined): string | null {
  const rgb = parseCssColorToRgb(input);
  return rgb ? formatHexColor(rgbToHex(rgb)) : null;
}

export type Lab = { l: number; a: number; b: number };

function rgbToLab(rgb: Rgb): Lab {
  const sr = rgb.r / 255;
  const sg = rgb.g / 255;
  const sb = rgb.b / 255;
  const linear = (c: number) => (c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4);
  const r = linear(sr);
  const g = linear(sg);
  const b = linear(sb);

  const x = r * 0.4124564 + g * 0.3575761 + b * 0.1804375;
  const y = r * 0.2126729 + g * 0.7151522 + b * 0.072175;
  const z = r * 0.0193339 + g * 0.119192 + b * 0.9503041;

  const refX = 0.95047;
  const refY = 1;
  const refZ = 1.08883;
  const f = (t: number) => (t > 0.008856 ? t ** (1 / 3) : 7.787 * t + 16 / 116);
  const fx = f(x / refX);
  const fy = f(y / refY);
  const fz = f(z / refZ);

  return {
    l: 116 * fy - 16,
    a: 500 * (fx - fy),
    b: 200 * (fy - fz),
  };
}

/** CIE76 ΔE — used for tight SVG color clustering (threshold calibrated for CIE76). */
export function deltaE(rgbA: Rgb, rgbB: Rgb): number {
  const labA = rgbToLab(rgbA);
  const labB = rgbToLab(rgbB);
  return Math.hypot(labA.l - labB.l, labA.a - labB.a, labA.b - labB.b);
}

/**
 * CIEDE2000 (ΔE₀₀) — current CIE/ISO perceptual color difference.
 * Prefer this for nearest-option matching; CIE76 underweights hue on saturated colors.
 * Implementation follows Sharma, Wu & Dalal (2005).
 */
export function deltaE2000(rgbA: Rgb, rgbB: Rgb): number {
  return deltaE2000Lab(rgbToLab(rgbA), rgbToLab(rgbB));
}

function deltaE2000Lab(lab1: Lab, lab2: Lab): number {
  const { l: L1, a: a1, b: b1 } = lab1;
  const { l: L2, a: a2, b: b2 } = lab2;

  const C1 = Math.hypot(a1, b1);
  const C2 = Math.hypot(a2, b2);
  const Cab = (C1 + C2) / 2;
  const Cab7 = Cab ** 7;
  const G = 0.5 * (1 - Math.sqrt(Cab7 / (Cab7 + 25 ** 7)));
  const a1p = (1 + G) * a1;
  const a2p = (1 + G) * a2;
  const C1p = Math.hypot(a1p, b1);
  const C2p = Math.hypot(a2p, b2);

  let h1p = Math.atan2(b1, a1p) * (180 / Math.PI);
  if (h1p < 0) h1p += 360;
  let h2p = Math.atan2(b2, a2p) * (180 / Math.PI);
  if (h2p < 0) h2p += 360;

  const dLp = L2 - L1;
  const dCp = C2p - C1p;
  let dhp = 0;
  if (C1p * C2p !== 0) {
    dhp = h2p - h1p;
    if (dhp > 180) dhp -= 360;
    else if (dhp < -180) dhp += 360;
  }
  const dHp = 2 * Math.sqrt(C1p * C2p) * Math.sin((dhp * Math.PI) / 360);

  const Lp = (L1 + L2) / 2;
  const Cp = (C1p + C2p) / 2;
  let hp: number;
  if (C1p * C2p === 0) {
    hp = h1p + h2p;
  } else if (Math.abs(h1p - h2p) <= 180) {
    hp = (h1p + h2p) / 2;
  } else {
    hp = (h1p + h2p < 360 ? h1p + h2p + 360 : h1p + h2p - 360) / 2;
  }

  const T =
    1 -
    0.17 * Math.cos(((hp - 30) * Math.PI) / 180) +
    0.24 * Math.cos((2 * hp * Math.PI) / 180) +
    0.32 * Math.cos(((3 * hp + 6) * Math.PI) / 180) -
    0.2 * Math.cos(((4 * hp - 63) * Math.PI) / 180);

  const Sl = 1 + (0.015 * (Lp - 50) ** 2) / Math.sqrt(20 + (Lp - 50) ** 2);
  const Sc = 1 + 0.045 * Cp;
  const Sh = 1 + 0.015 * Cp * T;
  const dRo = 30 * Math.exp(-(((hp - 275) / 25) ** 2));
  const Rc = 2 * Math.sqrt(Cp ** 7 / (Cp ** 7 + 25 ** 7));
  const Rt = -Rc * Math.sin((2 * dRo * Math.PI) / 180);

  const dL = dLp / Sl;
  const dC = dCp / Sc;
  const dH = dHp / Sh;
  return Math.sqrt(dL ** 2 + dC ** 2 + dH ** 2 + Rt * dC * dH);
}

/** Convert sRGB to HSL (h in degrees, s/l in 0–1). */
export function rgbToHsl(rgb: Rgb): Hsl {
  const r = rgb.r / 255;
  const g = rgb.g / 255;
  const b = rgb.b / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  if (max === min) {
    return { h: 0, s: 0, l };
  }
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h = 0;
  switch (max) {
    case r:
      h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
      break;
    case g:
      h = ((b - r) / d + 2) / 6;
      break;
    default:
      h = ((r - g) / d + 4) / 6;
      break;
  }
  return { h: h * 360, s, l };
}

/** Smallest circular distance between two hues in degrees (0–180). */
export function hueDistanceDegrees(a: number, b: number): number {
  const d = Math.abs(a - b) % 360;
  return Math.min(d, 360 - d);
}

/** LAB hue angle in degrees [0, 360) from a-star/b-star (better than HSL for yellow-green). */
export function labHueDegrees(rgb: Rgb): number {
  const { a, b } = rgbToLab(rgb);
  let h = Math.atan2(b, a) * (180 / Math.PI);
  if (h < 0) h += 360;
  return h;
}
